import { useTranslation } from 'react-i18next';
import type { ThresholdInfo } from './useTakataDealers';

/**
 * Port of `Pages/Shared/_CountryThresholdsPanel.cshtml`, shared by the dealer
 * dashboards exactly as the partial was.
 *
 * It shows the appointment-day cutoffs each market is judged against, which is
 * what makes a performance badge readable: "within deadline" means 15 days in
 * most countries and 7 in Poland.
 *
 * Which categories get a column depends on the campaign, so the caller passes
 * them. In After Sales, categories 1 to 4 are all day cutoffs (4 is "Critical",
 * the open-ended last band). In Takata, 4 is the inconclusive-outcome case and
 * not a day cutoff, so that dashboard shows 1 to 3. Category 0 (no appointment)
 * is never a column. The default is the Takata set, which the Razor partial
 * hard-coded for both screens; that is what hid Critical on After Sales.
 */

const DEFAULT_CATEGORIES: readonly number[] = [1, 2, 3];

export interface CountryThresholdsPanelProps {
  /** Keyed by ISO country code. */
  thresholdsByCountry: Record<string, ThresholdInfo[]>;
  /** Category indexes shown as columns, in order. Defaults to 1–3. */
  categories?: readonly number[];
  /** Extra class on the panel root, e.g. to give one campaign its own palette. */
  className?: string;
  open: boolean;
  onClose: () => void;
}

export function CountryThresholdsPanel({
  thresholdsByCountry,
  categories = DEFAULT_CATEGORIES,
  className,
  open,
  onClose,
}: CountryThresholdsPanelProps) {
  const { t } = useTranslation('mystery/dealerlist');

  const countries = Object.keys(thresholdsByCountry).sort();

  return (
    <div
      className={`country-thresholds-panel${className ? ` ${className}` : ''}${open ? ' visible' : ''}`}
      id="countryThresholdsPanel"
    >
      <div className="country-thresholds-header">
        <h4>{t('ThresholdsPanel.Title', 'Country thresholds')}</h4>
        <button
          type="button"
          className="country-thresholds-close"
          onClick={onClose}
          title={t('ThresholdsPanel.HideButton', 'Hide Thresholds')}
          aria-label={t('ThresholdsPanel.HideButton', 'Hide Thresholds')}
        >
          ×
        </button>
      </div>

      <div className="country-thresholds-content">
        <div className="country-thresholds-table-wrapper">
          <table className="country-thresholds-table">
            <thead>
              <tr>
                <th className="country-col">{t('ThresholdsPanel.Country', 'Country')}</th>
                {categories.map((category) => (
                  <th key={category} className={`threshold-col cat-${category}`}>
                    {t(`Threshold.Category.${category}.Name`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {countries.map((countryCode) => {
                const thresholds = thresholdsByCountry[countryCode];

                return (
                  <tr key={countryCode}>
                    <td className="country-col">
                      <span className="country-code">{countryCode}</span>
                    </td>
                    {categories.map((category) => {
                      const threshold = thresholds.find((x) => x.categoryIndex === category);

                      return (
                        <td key={category} className={`threshold-col cat-${category}`}>
                          {threshold ? (
                            <span className="threshold-value">{threshold.label}</span>
                          ) : (
                            <span className="threshold-value threshold-na">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
